import React, { useEffect, useState } from "react";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate } from "react-router-dom";

const PlatformPages: React.FC = () => {
 const { isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const [pages, setPages] = useState<any[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  fetchPages();
 }, [isAuthenticated, navigate]);

 const fetchPages = async () => {
  try {
   setLoading(true);
   setError(null);
   const response = await platformApiService.getPlatformPages();
   setPages(response.data.pages || []);
  } catch (err: any) {
   console.error("Error fetching pages:", err);
   setError(err.response?.data?.error || "Failed to load pages");
  } finally {
   setLoading(false);
  }
 };

 const handleDelete = async (pageId: string) => {
  if (!window.confirm("Are you sure you want to delete this page?")) {
   return;
  }

  try {
   await platformApiService.deletePlatformPage(pageId);
   fetchPages(); // Refresh the list
  } catch (err: any) {
   console.error("Error deleting page:", err);
   alert(err.response?.data?.error || "Failed to delete page");
  }
 };


 if (loading) {
  return <div className="p-8">Loading pages...</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6 flex justify-between items-center">
    <h1 className="text-3xl font-bold">Platform Pages</h1>
    <button
     onClick={() => navigate("/platform/pages/new")}
     className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700"
    >
     Create New Page
    </button>
   </div>

   {error && (
    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
     {error}
    </div>
   )}

   {pages.length === 0 ? (
    <div className="bg-white shadow p-8 text-center">
     <p className="text-gray-500">No pages found. Create your first page!</p>
    </div>
   ) : (
    <div className="bg-white shadow overflow-hidden">
     <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
       <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Title
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Slug
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Status
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Created
        </th>
        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
         Actions
        </th>
       </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
       {pages.map((page) => (
        <tr key={page._id} className="hover:bg-gray-50">
         <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900">{page.title}</div>
         </td>
         <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-500">{page.slug}</div>
         </td>
         <td className="px-6 py-4 whitespace-nowrap">
          <span
           className={`px-2 inline-flex text-xs leading-5 font-semibold ${
            page.isPublished
             ? "bg-green-100 text-green-800"
             : "bg-gray-100 text-gray-800"
           }`}
          >
           {page.isPublished ? "Published" : "Draft"}
          </span>
         </td>
         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {new Date(page.createdAt).toLocaleDateString()}
         </td>
         <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button
           onClick={() => navigate(`/platform/pages/${page.slug}`)}
           className="text-indigo-600 hover:text-indigo-900 mr-4"
          >
           Edit
          </button>
          <button
           onClick={() => handleDelete(page._id)}
           className="text-red-600 hover:text-red-900"
          >
           Delete
          </button>
         </td>
        </tr>
       ))}
      </tbody>
     </table>
    </div>
   )}

  </div>
 );
};

export default PlatformPages;



